import { PXS_MATRIX, MATRIX_BG, deriveProbabilityIndex, deriveSeverityIndex, matrixClassification, GOOD_OVERALL_THRESHOLD } from "@/lib/aepContent";

const P_LABELS = {
  1: "Improvável / baixa",
  2: "Possível",
  3: "Provável",
  4: "Frequente / evidenciada",
};
const S_LABELS = {
  1: "Leve",
  2: "Moderada",
  3: "Séria",
  4: "Crítica",
};

/**
 * Risk matrix P×S exactly as in the Sollute reference PDF.
 * Full-width, large, with the company's coordinates clearly highlighted.
 */
export default function RiskMatrix({
  overallScore = 0,
  dimensions = [],
  criticalCount = 0,
  totalResponses = 0,
  showLegend = true,
  strict = false,
}) {
  const worstScore = dimensions.length
    ? Math.max(...dimensions.filter((d) => d.responses > 0).map((d) => d.score), 0)
    : 0;

  const hasData = totalResponses > 0 && overallScore > 0;
  const p = hasData ? deriveProbabilityIndex(overallScore) : null;
  const s = hasData ? deriveSeverityIndex(worstScore, criticalCount, totalResponses, overallScore, strict) : null;
  const classification = hasData ? matrixClassification(p, s) : null;
  const bypassActive = !strict && hasData && overallScore <= GOOD_OVERALL_THRESHOLD;

  return (
    <div className="space-y-5" data-testid="risk-matrix">
      <table className="w-full border-collapse text-sm" style={{ tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th className="border border-slate-300 p-3 bg-slate-100 font-bold uppercase tracking-wider text-xs"
                style={{ width: "16%" }}>
              <div className="text-base">P × S</div>
              <div className="text-[10px] font-normal text-slate-500 normal-case mt-0.5">
                Probabilidade × Severidade
              </div>
            </th>
            {[1, 2, 3, 4].map((sIdx) => (
              <th key={sIdx}
                  style={{ width: "21%" }}
                  className={`border-2 ${s === sIdx ? "border-sollute-green bg-emerald-50" : "border-slate-300 bg-slate-100"} p-3 font-bold uppercase tracking-wider transition-colors`}>
                <div className="text-lg text-sollute-dark">S{sIdx}</div>
                <div className="text-[10px] font-normal text-slate-600 normal-case mt-0.5">{S_LABELS[sIdx]}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PXS_MATRIX.map(([pLabel, row], pIdxZero) => {
            const pIdx = pIdxZero + 1;
            return (
              <tr key={pLabel}>
                <th className={`border-2 ${p === pIdx ? "border-sollute-green bg-emerald-50" : "border-slate-300 bg-slate-100"} p-3 font-bold uppercase tracking-wider transition-colors`}>
                  <div className="text-lg text-sollute-dark">{pLabel}</div>
                  <div className="text-[10px] font-normal text-slate-600 normal-case mt-0.5">{P_LABELS[pIdx]}</div>
                </th>
                {row.map((cls, sIdxZero) => {
                  const sIdx = sIdxZero + 1;
                  const isHere = p === pIdx && s === sIdx;
                  return (
                    <td key={sIdx}
                        data-testid={`matrix-cell-P${pIdx}-S${sIdx}`}
                        className={`relative ${MATRIX_BG[cls]} border-2 ${isHere ? "border-sollute-green" : "border-slate-300"} p-5 text-center font-bold uppercase tracking-wider`}
                        style={{ minHeight: 90 }}>
                      <div className="text-base">{cls}</div>
                      {isHere && (
                        <>
                          <div className="absolute inset-0 ring-4 ring-sollute-green ring-inset pointer-events-none animate-pulse"></div>
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sollute-green text-white text-[10px] font-bold uppercase px-2 py-1 whitespace-nowrap shadow-lg z-10">
                            <i className="fa-solid fa-location-crosshairs mr-1"></i>Posição da empresa
                          </div>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {hasData ? (
        <div className="border-2 border-sollute-green bg-emerald-50 p-4 flex items-start gap-4">
          <i className="fa-solid fa-bullseye text-sollute-green text-3xl mt-1"></i>
          <div className="flex-1">
            <div className="overline text-sollute-green mb-1">Classificação da empresa na matriz</div>
            <div className="text-slate-800 text-sm">
              Posição{" "}
              <strong className="font-mono text-base text-sollute-dark">P{p} × S{s}</strong>{" "}
              — <strong className={`${MATRIX_BG[classification]} px-2 py-0.5 border text-xs uppercase tracking-wider`}>{classification}</strong>
            </div>
            <div className="text-xs text-slate-600 mt-2 leading-relaxed">
              <strong>Probabilidade {p}/4</strong> ({P_LABELS[p]}) · <strong>Severidade {s}/4</strong> ({S_LABELS[s]}).
              Baseado em score consolidado de <strong>{overallScore}</strong> em {totalResponses} resposta(s),
              com {criticalCount} resposta(s) crítica(s) (≥4,5).
            </div>
            {bypassActive && (
              <div className="text-[11px] text-emerald-900 bg-white/60 border border-emerald-200 mt-2 px-2 py-1.5 leading-snug"
                   data-testid="risk-matrix-bypass-note">
                <i className="fa-solid fa-circle-info mr-1"></i>
                <strong>Bypass inteligente ativo:</strong> a média geral está dentro
                da faixa <em>Tolerável</em> (≤ {GOOD_OVERALL_THRESHOLD.toFixed(1)}), portanto
                respostas críticas isoladas não inflam a Severidade. A classificação
                reflete a percepção coletiva, não pontos fora da curva.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 text-center">
          <i className="fa-solid fa-info-circle mr-2"></i>
          Aguardando respostas para posicionar a empresa na matriz.
        </div>
      )}

      {showLegend && (
        <div className="flex flex-wrap gap-2 items-center text-xs">
          <span className="overline">Legenda:</span>
          {Object.entries(MATRIX_BG).map(([label, cls]) => (
            <span key={label} className={`${cls} border px-3 py-1 font-bold uppercase tracking-wider`}>{label}</span>
          ))}
        </div>
      )}
    </div>
  );
}
